import { Button, Dialog } from '@noorden-insurtech/alba-design-system';
import { ClipboardCopy } from 'lucide-react';
import { useState } from 'react';
import { CopyableField } from '@/shared/ui';

interface JsonViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  requestPayload?: unknown;
  responsePayload?: unknown;
  /** Trace da chamada que falhou (Sentry). Ausente em linhas de sucesso. */
  traceId?: string;
  /** `x-request-id` da requisição que falhou. Ausente em linhas de sucesso. */
  requestId?: string;
}

type Tab = 'request' | 'response';

function formatJson(data: unknown): string {
  if (!data) return 'Nenhum dado disponível';
  try {
    if (typeof data === 'string') {
      return JSON.stringify(JSON.parse(data), null, 2);
    }
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

export function JsonViewerModal({
  isOpen,
  onClose,
  title,
  requestPayload,
  responsePayload,
  traceId,
  requestId,
}: JsonViewerModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('request');
  const [copied, setCopied] = useState(false);

  const content =
    activeTab === 'request'
      ? formatJson(requestPayload)
      : formatJson(responsePayload);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => {
        if (!details.open) onClose();
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[720px] max-w-[90vw] max-h-[80vh] flex flex-col'>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>

          <div className='flex gap-2 px-6 pb-3'>
            <button
              type='button'
              onClick={() => setActiveTab('request')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'request'
                  ? 'bg-alba-blue-500 text-white'
                  : 'bg-alba-gray-100 text-alba-gray-600 hover:bg-alba-gray-200'
              }`}
            >
              Request
            </button>
            <button
              type='button'
              onClick={() => setActiveTab('response')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'response'
                  ? 'bg-alba-blue-500 text-white'
                  : 'bg-alba-gray-100 text-alba-gray-600 hover:bg-alba-gray-200'
              }`}
            >
              Response
            </button>
          </div>

          {(traceId || requestId) && (
            <div className='flex flex-wrap items-center gap-x-4 gap-y-1 px-6 pb-3 text-sm text-alba-gray-600'>
              {requestId && (
                <span className='inline-flex items-center gap-2'>
                  Requisição:
                  <CopyableField value={requestId} label='Request ID' truncate />
                </span>
              )}
              {traceId && (
                <span className='inline-flex items-center gap-2'>
                  Trace:
                  <CopyableField value={traceId} label='Trace ID' truncate />
                </span>
              )}
            </div>
          )}

          <Dialog.Body className='flex-1 overflow-hidden px-6 pb-4'>
            <div className='relative h-full'>
              <button
                type='button'
                onClick={handleCopy}
                className='absolute top-2 right-2 flex items-center gap-1 px-2 py-1 text-xs rounded bg-alba-gray-200 hover:bg-alba-gray-300 text-alba-gray-700 transition-colors z-10'
                title='Copiar JSON'
              >
                <ClipboardCopy className='w-3 h-3' />
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
              <pre className='bg-alba-gray-50 border border-alba-gray-200 rounded-lg p-4 text-xs font-mono text-alba-gray-800 overflow-auto max-h-[50vh] whitespace-pre-wrap break-words'>
                {content}
              </pre>
            </div>
          </Dialog.Body>

          <Dialog.Footer>
            <Button variant='ghost' onClick={onClose}>
              Fechar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
